import React, { useEffect, useState } from "react";
import { Box, CircularProgress, ClickAwayListener, IconButton, TableCell, TableRow } from "@mui/material";
import { DotLegalTable, DotLegalTextField, ITableHeader } from "@dotlegal/dotlegal-ui-components";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import { useTranslation } from "@localization/useTranslation";
import DocumentPreviewLink from "@common/components/documentPreview/DocumentPreviewLink";
import { isNullOrWhitespace } from "@common/stringOperations";
import { RiskAssessmentDocumentUpdateModel, RiskAssessmentDocumentViewModel } from "./RiskDocumentationPane.types";

export interface IRiskAssessmentDocumentTable {
    documents: Array<RiskAssessmentDocumentViewModel>;
    onRemoveDocument: (documentId: string) => Promise<void>;
    onEditDocument: (documentId: string, update: RiskAssessmentDocumentUpdateModel) => void;
    onDocumentClick: (document: RiskAssessmentDocumentViewModel) => void;
    getPreviewHref: (document: RiskAssessmentDocumentViewModel) => string | undefined;
}

// A link document has no stored file (dataId is empty); its label is the link URL, and editing it changes the
// link rather than a file name.
const getLabel = (document: RiskAssessmentDocumentViewModel) => document.fileName ?? document.link ?? "";

function RiskAssessmentDocumentTable(props: IRiskAssessmentDocumentTable) {
    const { translateString, translateDate } = useTranslation();

    // Which row is in inline-rename mode is purely table-local UI state.
    const [selectedDocument, setSelectedDocument] = useState<RiskAssessmentDocumentViewModel | undefined>(undefined);

    // The document whose deletion is in flight, so its row swaps the delete icon for a spinner until the
    // refetch settles.
    const [deletingDocumentId, setDeletingDocumentId] = useState<string | undefined>(undefined);

    const handleRemoveDocument = async (documentId: string) => {
        setDeletingDocumentId(documentId);
        try {
            await props.onRemoveDocument(documentId);
        } finally {
            setDeletingDocumentId(undefined);
        }
    };

    const textFieldRef = React.useRef<HTMLTextAreaElement>();
    useEffect(() => {
        if (selectedDocument) {
            textFieldRef.current?.focus();
            // Preselect the base name (up to the extension dot) so a rename doesn't require clearing the extension.
            const dotIndex = textFieldRef.current?.value.lastIndexOf(".") ?? -1;
            const endIndex = dotIndex > 0 ? dotIndex : textFieldRef.current?.value.length;
            textFieldRef.current?.setSelectionRange(0, endIndex!);
        }
    }, [selectedDocument]);

    const headers: Array<ITableHeader<RiskAssessmentDocumentViewModel>> = [
        { property: "fileName", text: translateString("documentation"), align: "left", showOnMobile: true },
        { property: "created", text: translateString("date"), align: "left", showOnMobile: true },
        { property: null, text: "" },
    ];

    const handleRowClick = (row: RiskAssessmentDocumentViewModel) => {
        if (textFieldRef.current) return;
        props.onDocumentClick(row);
    };

    const onLabelChange = (row: RiskAssessmentDocumentViewModel, value: string) => {
        if (isNullOrWhitespace(row.dataId)) {
            props.onEditDocument(row.documentId, { fileName: undefined, link: value });
        } else {
            props.onEditDocument(row.documentId, { fileName: value, link: undefined });
        }
    };

    return (
        <Box>
            <DotLegalTable
                headers={headers}
                emptyText={translateString("noDocuments")}
                paginationLabelOf={translateString("labelOf")}
                defaultOrderBy={"created"}
                defaultOrder={"desc"}
                isLoading={false}
                hidePagination
                textStyle={"NoCapsHeaders"}
                data={props.documents}
                marginTop={1}
                clickableRows={selectedDocument === undefined}
                rowsPerPage={1000}
                renderRow={(row) => (
                    <TableRow
                        sx={{
                            "&:hover": {
                                button: {
                                    visibility: "visible",
                                },
                            },
                        }}
                        key={row.documentId}
                        onClick={() => handleRowClick(row)}
                    >
                        {selectedDocument?.documentId === row.documentId ? (
                            <ClickAwayListener onClickAway={() => setSelectedDocument(undefined)}>
                                <TableCell>
                                    <Box sx={{ mt: "5px" }}>
                                        <DotLegalTextField
                                            noMargin
                                            variant="standard"
                                            label={""}
                                            value={getLabel(row)}
                                            debounce
                                            inputRef={textFieldRef}
                                            onChange={(value) => onLabelChange(row, value)}
                                            onEnterPressed={() => setSelectedDocument(undefined)}
                                        />
                                    </Box>
                                </TableCell>
                            </ClickAwayListener>
                        ) : (
                            <TableCell>
                                <DocumentPreviewLink previewHref={props.getPreviewHref(row)} onPreviewClick={() => props.onDocumentClick(row)}>
                                    {getLabel(row)}
                                </DocumentPreviewLink>
                            </TableCell>
                        )}

                        <TableCell sx={{ whiteSpace: "nowrap", width: 100 }}>{translateDate(row.created)}</TableCell>

                        <TableCell width={40}>
                            <Box sx={{ display: "flex" }}>
                                <IconButton
                                    sx={{ visibility: "hidden" }}
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedDocument(selectedDocument ? undefined : row);
                                    }}
                                >
                                    <EditIcon />
                                </IconButton>

                                {deletingDocumentId === row.documentId ? (
                                    <CircularProgress sx={{ margin: "5px" }} size={20} />
                                ) : (
                                    <IconButton
                                        sx={{ visibility: "hidden" }}
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleRemoveDocument(row.documentId);
                                        }}
                                    >
                                        <CloseIcon sx={(theme) => ({ fill: theme.palette.error.main })} />
                                    </IconButton>
                                )}
                            </Box>
                        </TableCell>
                    </TableRow>
                )}
            />
        </Box>
    );
}

export default RiskAssessmentDocumentTable;
